import { useEffect, useRef, useState } from 'react';
import { Group } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { CustomNumberInput } from './custom-number-input';
import { inputMessages } from './input-messages';
import styles from './weight-input.module.css';
import {
  combineKilogramsAndGrams,
  combinePoundsAndOunces,
  getMajorUnitType,
  getWeightUnitMessage,
  isImperialWeight,
  normalizeToMajorWeightUnit,
  splitWeight,
} from '../../utils/weight-utils';

import type { Weight } from '../../types/weight';
import type { MantineSize } from '@mantine/core';

export type WeightInputProps = {
  value: Weight;
  /** Fires on every change with the combined lbs+oz or kg+g value. */
  onChange?: (next: Weight) => void;
  /** Fires on blur with the combined lbs+oz or kg+g value.*/
  onBlur?: (next: Weight) => void;
  disabled?: boolean;
  size?: MantineSize;
  /** When false, hides the minor unit input (oz/g). Defaults to true. */
  showMinorUnit?: boolean;
  /** Inline error message shown on the major unit input. */
  error?: string;
  withAsterisk?: boolean;
};

export const WeightInput = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  size,
  showMinorUnit = true,
  error,
  withAsterisk = false,
}: WeightInputProps) => {
  const intl = useIntl();

  const baseUnit = value.unit;
  const majorUnit = getMajorUnitType(baseUnit);
  const isImperial = isImperialWeight(baseUnit);

  const initialWeight = showMinorUnit
    ? splitWeight(value)
    : { major: normalizeToMajorWeightUnit(value, { roundUp: true }), minor: { value: 0, unit: baseUnit } };
  const [majorValue, setMajorValue] = useState<number | string>(initialWeight.major.value);
  const [minorValue, setMinorValue] = useState<number | string>(initialWeight.minor.value);

  useEffect(() => {
    // When the minor unit (oz/g) is hidden, any fractional remainder can't be shown or
    // edited, so round the major value up rather than silently dropping it.
    const _updatedWeight = { value: value.value, unit: value.unit };
    const updatedWeight = showMinorUnit
      ? splitWeight(_updatedWeight)
      : { major: normalizeToMajorWeightUnit(_updatedWeight, { roundUp: true }), minor: { value: 0, unit: value.unit } };
    setMajorValue((prev) => (Number(prev) === updatedWeight.major.value ? prev : updatedWeight.major.value));
    setMinorValue((prev) => (Number(prev) === updatedWeight.minor.value ? prev : updatedWeight.minor.value));
  }, [value.value, value.unit, showMinorUnit]);

  const toWeightValue = (major: number | string, minor: number | string): Weight => {
    const majorNum = typeof major === 'string' ? parseFloat(major) || 0 : major;
    const minorNum = typeof minor === 'string' ? parseFloat(minor) || 0 : minor;
    const combined = isImperial ? combinePoundsAndOunces(majorNum, minorNum) : combineKilogramsAndGrams(majorNum, minorNum);
    return { value: combined, unit: baseUnit };
  };

  const commitWeight = () => {
    onBlur?.(toWeightValue(majorValue, minorValue));
  };

  const commitRef = useRef(commitWeight);
  commitRef.current = commitWeight;

  const handleMajorChange = (next: number | string) => {
    setMajorValue(next);
    onChange?.(toWeightValue(next, minorValue));
  };

  const handleMinorChange = (next: number | string) => {
    setMinorValue(next);
    onChange?.(toWeightValue(majorValue, next));
  };

  const handleMajorBlur = () => commitRef.current();
  const handleMinorBlur = () => commitRef.current();

  return (
    <Group align='flex-end' gap='sm'>
      <CustomNumberInput
        allowLeadingZeros={false}
        allowNegative={false}
        aria-label={intl.formatMessage(inputMessages.weightLabel)}
        className={styles.inputFlex}
        data-testid='package-weight-major'
        decimalScale={0}
        disabled={disabled}
        error={error}
        label={<FormattedMessage {...inputMessages.weightLabel} />}
        min={0}
        onBlur={handleMajorBlur}
        onChange={handleMajorChange}
        placeholder={intl.formatMessage(inputMessages.zeroPlaceholder)}
        rightSectionWidth={50}
        size={size}
        step={1}
        internalLabel={intl.formatMessage(getWeightUnitMessage(majorUnit))}
        value={majorValue}
        withAsterisk={withAsterisk}
      />
      {showMinorUnit && (
        <CustomNumberInput
          allowLeadingZeros={false}
          allowNegative={false}
          aria-label={intl.formatMessage(getWeightUnitMessage(baseUnit))}
          className={styles.inputFlex}
          data-testid='package-weight-minor'
          decimalScale={2}
          disabled={disabled}
          min={0}
          onBlur={handleMinorBlur}
          onChange={handleMinorChange}
          placeholder={intl.formatMessage(inputMessages.zeroPlaceholder)}
          rightSectionWidth={50}
          size={size}
          step={1}
          internalLabel={intl.formatMessage(getWeightUnitMessage(baseUnit))}
          value={minorValue}
        />
      )}
    </Group>
  );
};
